<template>
  <div
    v-if="editor"
    class="tiptap-wrapper w-full h-full flex flex-col overflow-y-auto rounded-2xl bg-background"
    :class="getClasses()"
  >
    <header
      v-if="mode === 'edit' || mode === 'view'"
      class="flex justify-between items-center gap-3 px-6 py-1 border-b border-border"
    >
      <el-scrollbar>
        <div class="flex items-center gap-3" style="height: 36px">
          <TipTapToolbar v-if="mode === 'edit'" :editor="editor" />
        </div>
      </el-scrollbar>
      <template v-if="mode === 'edit'">
        <div class="flex items-center gap-2 flex-shrink-0">
          <div class="toolbar-divider"></div>
          <DropdownMenu v-if="templateItems.length">
            <el-tooltip content="Шаблоны" :show-after="300" placement="top">
              <DropdownMenuTrigger as-child>
                <button class="toolbar-btn relative">
                  <svg-icon
                    name="huge-book"
                    class="w-4 h-4 fill-none text-foreground"
                  />
                  <span
                    v-if="templateItems.length"
                    class="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-[10px] rounded-full flex items-center justify-center animate-pulse"
                  >
                    {{ templateItems.length }}
                  </span>
                </button>
              </DropdownMenuTrigger>
            </el-tooltip>

            <DropdownMenuContent
              align="top"
              class="w-120 max-h-72 overflow-y-auto"
            >
              <DropdownMenuItem
                v-for="item in templateItems"
                :key="item.code"
                @click="selectTemplate(item.code)"
                class="flex items-center justify-between gap-2 leading-4 text-muted-foreground"
              >
                <span
                  :class="
                    item.code === activeTemplateCode ? 'text-foreground' : ''
                  "
                >
                  {{ item.name }}
                </span>
                <svg
                  v-if="item.code === activeTemplateCode"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-primary shrink-0"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <el-tooltip content="Предпросмотр" :show-after="300" placement="top">
            <button
              v-if="showPreviewButton"
              @click="getPreviewContent"
              class="toolbar-btn"
            >
              <svg-icon
                name="huge-editor-preview"
                class="w-4 h-4 fill-none text-foreground"
              />
            </button>
          </el-tooltip>
          <DropdownMenu>
            <el-tooltip
              content="Язык шаблона"
              :show-after="300"
              placement="top"
            >
              <DropdownMenuTrigger as-child>
                <button class="toolbar-btn gap-1">
                  <span class="text-xs text-foreground">
                    {{ templateLangTitle }}
                  </span>
                  <el-icon class="w-3 h-3 text-foreground">
                    <arrow-down />
                  </el-icon>
                </button>
              </DropdownMenuTrigger>
            </el-tooltip>
            <DropdownMenuContent>
              <DropdownMenuItem
                v-for="loc in langs"
                :key="loc.id"
                :class="loc.value === templateLang ? 'text-foreground' : ''"
                class="text-muted-foreground"
                @click="changeLang(loc.value)"
              >
                {{ loc.title }}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                @click="$emit('update:isBothLanguages', !isBothLanguages)"
              >
                <div class="flex items-center gap-2">
                  <div
                    class="w-4 h-4 rounded border border-border flex items-center justify-center"
                    :class="isBothLanguages ? 'bg-primary border-primary' : ''"
                  >
                    <svg
                      v-if="isBothLanguages"
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <span>Для обоих языков</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </template>
      <template v-if="mode === 'view'">
        <div class="flex items-center gap-2 ml-auto">
          <div class="flex items-center gap-0.5 rounded-md bg-muted p-0.5">
            <button
              v-for="loc in langs"
              :key="loc.id"
              class="px-2.5 py-1.5 text-xs rounded-md transition-colors"
              :class="
                loc.value === templateLang
                  ? 'bg-background text-foreground shadow-sm font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              "
              @click="changeLang(loc.value)"
            >
              {{ loc.title }}
            </button>
          </div>
        </div>
      </template>
    </header>

    <!-- EDITOR -->
    <div
      class="editor-shell flex-1 h-full overflow-y-auto relative bg-muted/30"
      @mousemove="onEditorMouseMove"
      @mouseleave="hideDragHandle"
      @dragover.prevent
      @drop="onBlockDrop"
      style="background-color: hsl(var(--sidebar-background))"
    >
      <div class="page a4">
        <div class="relative">
          <editor-content :editor="editor" class="tiptap" />

          <!-- Selection floating toolbar -->
          <SelectionToolbar
            v-if="mode === 'edit'"
            :editor="editor"
            :default-participant-code="defaultParticipantCode"
          />

          <!-- Table row/column controls -->
          <TableContextToolbar v-if="mode === 'edit'" :editor="editor" />

          <!-- Drag Handle -->
          <DragHandle
            v-if="mode === 'edit'"
            :editor="editor"
            :node="dragHandleNode"
            :pos="dragHandlePos"
            :visible="dragHandleVisible"
            :style="dragHandlePosition"
            @lock="lockDragHandle"
            @unlock="unlockDragHandle"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";

import { ListItem } from "@tiptap/extension-list";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import FontFamily from "@tiptap/extension-font-family";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import {
  MentionNode,
  MentionSuggestion,
} from "@/shared/ui/tiptap/nodes/MentionNode";
import { SignatureNode } from "@/shared/ui/tiptap/nodes/SignatureNode";
import { RepeatSectionNode } from "@/shared/ui/tiptap/nodes/RepeatSectionNode";

import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";

import FontSize from "@/shared/ui/tiptap/extensions/FontSize";
import Indent from "@/shared/ui/tiptap/extensions/Indent";
import LineSpacing from "@/shared/ui/tiptap/extensions/LineSpacing";
import DocumentSettings from "@/shared/ui/tiptap/extensions/DocumentSettings";
import ParagraphSpacing from "@/shared/ui/tiptap/extensions/ParagraphSpacing";

import MentionService from "@/shared/mention/MentionService";
import { createFieldsMap, findFieldValue } from "@/shared/mention/fieldMatcher";
import {
  numberToWords,
  extractBaseKeyFromCalc,
} from "@/shared/lib/numberToWords";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/shared/ui/dropdown-menu";
import SlashCommands from "@/shared/ui/tiptap/extensions/SlashCommands";

import Button from "@/shared/ui/button/Button.vue";
import TipTapToolbar from "./TipTapToolbar.vue";
import TableContextToolbar from "./toolbar/TableContextToolbar.vue";
import SelectionToolbar from "./toolbar/SelectionToolbar.vue";
import DragHandle from "./DragHandle.vue";

export default {
  name: "TipTapEditor2",
  components: {
    EditorContent,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,

    Button,
    TipTapToolbar,
    TableContextToolbar,
    SelectionToolbar,
    DragHandle,
  },

  props: {
    json: Object,
    mode: {
      type: String,
      default: "edit",
    },
    templateLang: {
      type: String,
      default: "ru",
    },
    mentionItems: {
      type: Array,
      default: () => [],
    },
    showPreviewButton: {
      type: Boolean,
      default: false,
    },
    withoutPadding: {
      type: Boolean,
      default: false,
    },
    isBothLanguages: {
      type: Boolean,
      default: false,
    },
    signatures: {
      type: Array,
      default: () => [],
    },
    templateItems: {
      type: Array,
      default: () => [],
    },
    activeTemplateCode: {
      type: String,
      default: "",
    },
    defaultParticipantCode: {
      type: String,
      default: null,
    },
    allowUnwrapRepeatSection: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      editor: null,
      editingMention: null,
      // Drag handle state
      dragHandleVisible: false,
      dragHandleNode: null,
      dragHandlePos: 0,
      dragHandlePosition: {},
      dragHandleLocked: false,
    };
  },

  computed: {
    templateLangTitle() {
      const titles = {
        ru: "Рус",
        kz: "Қаз",
        en: "Eng",
      };
      return titles[this.templateLang] || "Рус";
    },
    langs() {
      return [
        { id: 1, title: "Рус", value: "ru" },
        { id: 2, title: "Қаз", value: "kz" },
        { id: 3, title: "Eng", value: "en" },
      ];
    },
  },

  watch: {
    mode(newMode) {
      if (this.editor) {
        this.editor.setEditable(newMode === "edit");
        this.editor.extensionManager.extensions.forEach((ext) => {
          if (ext.options && "mode" in ext.options) {
            ext.options.mode = newMode;
          }
        });
        this.editor.view.dispatch(this.editor.state.tr);
      }
    },
    signatures: {
      handler(newSignatures) {
        if (!this.editor) return;
        this.editor.storage.signature.signatures = newSignatures || [];
        this.refreshSignatureNodes();
      },
      deep: true,
      immediate: true,
    },
    json: {
      handler(content) {
        if (this.editor && content) {
          const currentJSON = this.getDocument();

          if (JSON.stringify(content) !== JSON.stringify(currentJSON)) {
            this.editor.commands.setContent(content);
            this.$nextTick(() => this.refreshSignatureNodes());
          }
        }
      },
      deep: true,
    },
  },

  mounted() {
    this.initEditor();
  },

  beforeUnmount() {
    this.editor?.destroy();
  },

  methods: {
    // --- Жизненный цикл редактора ---

    initEditor() {
      this.editor = new Editor({
        editable: this.mode === "edit",
        content: this.json || {
          type: "doc",
          content: [{ type: "paragraph" }],
        },
        extensions: [
          StarterKit.configure({
            heading: { levels: [1, 2, 3, 4, 5, 6] },
          }),
          Placeholder.configure({
            placeholder: "Введите '/' для команд…",
            showOnlyWhenEditable: true,
          }),

          Color.configure({ types: [TextStyle.name, ListItem.name] }),
          TextStyle.configure({ types: [ListItem.name] }),
          Link.configure({ openOnClick: false }),
          TextAlign.configure({ types: ["heading", "paragraph"] }),
          FontFamily,
          Underline,
          Highlight.configure({ multicolor: true }),
          FontSize,
          Indent,
          LineSpacing,
          DocumentSettings,
          ParagraphSpacing,

          Table.configure({
            resizable: true,
            handleWidth: 5,
            cellMinWidth: 40,
            lastColumnResizable: true,
          }),
          TableRow,
          TableHeader,
          TableCell,

          MentionNode.configure({ mode: this.mode }),
          MentionSuggestion({ items: this.mentionItems }),
          SignatureNode.configure({ mode: this.mode }),
          RepeatSectionNode.configure({
            mode: this.mode,
            allowUnwrap: this.allowUnwrapRepeatSection,
          }),
          SlashCommands,
        ],
        editorProps: {
          handleClick: (view, pos, event) => {
            const node = view.state.doc.nodeAt(pos);
            if (node?.type?.name === "mention") {
              this.openMentionEditor({
                node,
                pos,
                clientRect: event.target.getBoundingClientRect(),
              });
              return true;
            }
            return false;
          },
        },
        onUpdate: ({ editor }) => {
          this.$emit("update:json", editor.getJSON());
          this.extractMentions();
        },
      });

      if (this.signatures?.length) {
        this.editor.storage.signature.signatures = this.signatures;
      }
    },

    getDocument() {
      return this.editor ? this.editor.getJSON() : null;
    },

    getClasses() {
      return this.withoutPadding ? "without-padding" : "";
    },

    // --- Язык и шаблоны ---

    changeLang(lang) {
      this.$emit("change:lang", lang);
    },

    selectTemplate(code) {
      this.$emit("select:template", code);
    },

    // --- CRUD для Mention ---

    extractMentions() {
      const mentions = [];
      this.editor.state.doc.descendants((node) => {
        if (node.type.name === "mention") {
          mentions.push({
            key: node.attrs?.key,
            label: node.attrs?.label,
          });
        }
      });
      this.$emit("mentions", mentions);
      return mentions;
    },

    insertServiceField(field) {
      if (!this.editor || !field?.key || field.value == null) return;

      const { key, service_code, value, participant_type_code } = field;
      const displayKey = MentionService.generateDisplayKey(
        service_code,
        key,
        participant_type_code
      );

      let found = false;
      this.editor.state.doc.descendants((node, pos) => {
        if (node.type.name !== "mention") return;

        const nodeDisplayKey = MentionService.generateDisplayKey(
          node.attrs?.service_code,
          node.attrs?.key,
          node.attrs?.participant_type_code
        );

        if (nodeDisplayKey === displayKey) {
          this.editor
            .chain()
            .setNodeSelection(pos)
            .updateAttributes("mention", {
              ...node.attrs,
              label: node.attrs?.label || key,
              displayKey,
              value,
            })
            .run();
          found = true;
        }
      });

      if (!found) {
        this.$message({
          type: "warning",
          message: `В шаблоне "${displayKey || key}" mention не найден.`,
        });
      }
    },

    insertCustomServiceField(field) {
      if (!this.editor || !field?.key || !field.value || field.value == null)
        return;

      const { key, value } = field;
      let found = false;

      this.editor.state.doc.descendants((node, pos) => {
        if (node.type.name === "mention" && node.attrs.key === key) {
          this.editor
            .chain()
            .setNodeSelection(pos)
            .updateAttributes("mention", {
              ...node.attrs,
              label: node.attrs?.label || key,
              value,
            })
            .run();
          found = true;
          this.$message({
            type: "warning",
            message: `Метка с ключом "${key}" была обновлена.`,
          });
        }
      });

      if (!found) {
        this.$message({
          type: "message",
          message: `Метка с ключом "${key}" не найдена в шаблоне.`,
        });
      }
    },

    insertMentionByKey(key, label = "", service_code, participant_type_code) {
      if (!this.editor || !key) return;

      const payload = MentionService.createMentionPayload({
        key,
        label,
        service_code,
        participant_type_code,
      });

      const validation = MentionService.validateMentionAttributes(payload);
      if (!validation.valid) return;

      this.editor
        .chain()
        .focus()
        .insertContent([
          { type: "mention", attrs: payload },
          { type: "text", text: " " },
        ])
        .run();
    },

    insertMentionByType(data) {
      if (!this.editor || !data.key) return;

      const {
        key,
        label = "",
        service_code,
        participant_type_code,
        isCustom,
        isNotary,
      } = data;

      const payload = MentionService.createMentionPayload({
        key,
        label,
        service_code,
        participant_type_code,
        isCustom: isCustom || false,
        isNotary: isNotary || false,
      });

      const validation = MentionService.validateMentionAttributes(payload);
      if (!validation.valid) return;

      this.editor.commands.insertCustomMention(payload);
    },

    /**
     * BULK: обновляет все совпадающие mentions
     */
    insertServiceFields(fields = []) {
      if (!this.editor || !Array.isArray(fields) || !fields.length) return;

      const fieldsMap = createFieldsMap(fields);
      if (!fieldsMap.size) return;

      let updatedCount = 0;
      let clearedCount = 0;

      this.editor.state.doc.descendants((node, pos) => {
        if (node.type.name !== "mention") return;

        const attrs = node.attrs;

        // Обработка isNumberToWords (calc) mentions
        if (attrs.isNumberToWords) {
          const baseKey = extractBaseKeyFromCalc(attrs.key);
          if (baseKey) {
            const matchInfo = findFieldValue(
              fieldsMap,
              attrs.service_code,
              baseKey,
              attrs.participant_type_code
            );

            if (matchInfo && matchInfo.value) {
              try {
                const wordsValue = numberToWords(
                  matchInfo.value,
                  this.templateLang
                );
                this.editor
                  .chain()
                  .setNodeSelection(pos)
                  .updateAttributes("mention", {
                    ...attrs,
                    value: wordsValue,
                    isLoading: false,
                  })
                  .run();
                updatedCount++;
              } catch (error) {
                console.error(`Ошибка при обновлении calc mention:`, error);
              }
            }
          }
          return;
        }

        const matchInfo = findFieldValue(
          fieldsMap,
          attrs.service_code,
          attrs.key,
          attrs.participant_type_code
        );

        if (matchInfo) {
          try {
            const newValue = matchInfo.value || "";
            this.editor
              .chain()
              .setNodeSelection(pos)
              .updateAttributes("mention", {
                ...attrs,
                value: newValue,
                isLoading: false,
              })
              .run();
            if (newValue) updatedCount++;
            else clearedCount++;
          } catch (error) {
            console.error(`Ошибка при обновлении mention:`, error);
          }
        }
      });

      if (updatedCount + clearedCount === 0) {
        console.warn("Не найдено совпадений для mention узлов");
      }
    },

    /**
     * Помечает пустые mention с совпадающим service_code и participant_type_code
     * как "loading" — рендер покажет skeleton-полоску.
     */
    setMentionsLoading({ serviceCode, participantTypeCode } = {}) {
      if (!this.editor || !serviceCode) return;
      this.editor.state.doc.descendants((node, pos) => {
        if (node.type.name !== "mention") return;
        const a = node.attrs;
        if (a.service_code !== serviceCode) return;
        if (
          participantTypeCode &&
          a.participant_type_code !== participantTypeCode
        )
          return;
        if (a.isLoading) return;
        this.editor
          .chain()
          .setNodeSelection(pos)
          .updateAttributes("mention", { ...a, isLoading: true })
          .run();
      });
    },

    /**
     * Снимает loading-флаг с mention. Без параметров — со всех узлов;
     * с serviceCode/participantTypeCode — только с подходящих.
     */
    clearMentionsLoading({ serviceCode, participantTypeCode } = {}) {
      if (!this.editor) return;
      this.editor.state.doc.descendants((node, pos) => {
        if (node.type.name !== "mention") return;
        const a = node.attrs;
        if (!a.isLoading) return;
        if (serviceCode && a.service_code !== serviceCode) return;
        if (
          participantTypeCode &&
          a.participant_type_code !== participantTypeCode
        )
          return;
        this.editor
          .chain()
          .setNodeSelection(pos)
          .updateAttributes("mention", { ...a, isLoading: false })
          .run();
      });
    },

    // --- RepeatSection ---

    wrapInRepeatSection(placeholderCode, label) {
      if (!this.editor) return;
      this.editor.commands.wrapInRepeatSection({
        placeholderCode,
        label: label || placeholderCode,
      });
    },

    unwrapRepeatSection() {
      if (!this.editor) return;
      this.editor.commands.unwrapRepeatSection();
    },

    // --- Редактор Mention ---

    openMentionEditor({ node, pos, clientRect }) {
      this.editingMention = { node, pos, rect: clientRect };

      this.$emit("edit:mention", {
        participant_type_code: node.attrs.participant_type_code,
        hasValue: !!(node.attrs.value && node.attrs.value.trim()),
        onSave: this.updateMentionParticipant,
        onDelete: this.deleteMention,
        onRestore: this.restoreMentionValue,
        onNumberToWords: this.duplicateAsNumberToWords,
        rect: clientRect,
      });
    },

    updateMentionParticipant(participant_type_code) {
      const { pos, node } = this.editingMention;
      const displayKey = MentionService.generateDisplayKey(
        node.attrs.service_code,
        node.attrs.key,
        participant_type_code
      );

      this.editor
        .chain()
        .focus()
        .setNodeSelection(pos)
        .updateAttributes("mention", {
          ...node.attrs,
          participant_type_code,
          displayKey,
        })
        .run();

      this.editingMention = null;
    },

    deleteMention() {
      const { pos } = this.editingMention;
      this.editor
        .chain()
        .focus()
        .deleteRange({ from: pos, to: pos + 1 })
        .run();
      this.editingMention = null;
    },

    restoreMentionValue() {
      const { pos, node } = this.editingMention;
      if (!node) return;

      this.editor
        .chain()
        .focus()
        .setNodeSelection(pos)
        .updateAttributes("mention", { ...node.attrs, value: "" })
        .run();
      this.editingMention = null;
    },

    duplicateAsNumberToWords() {
      if (!this.editingMention) return;

      const { node, pos } = this.editingMention;
      const attrs = node.attrs;
      const insertPos = pos + node.nodeSize;

      this.editor
        .chain()
        .focus()
        .insertContentAt(insertPos, [
          { type: "text", text: " " },
          {
            type: "mention",
            attrs: {
              key: `calc{${attrs.key}}`,
              displayKey: `calc{${attrs.displayKey || attrs.key}}`,
              label: attrs.label,
              service_code: attrs.service_code,
              participant_type_code: attrs.participant_type_code,
              isCustom: attrs.isCustom || false,
              isNotary: attrs.isNotary || false,
              isNumberToWords: true,
            },
          },
          { type: "text", text: " " },
        ])
        .run();

      this.editingMention = null;
    },

    // --- Подписи ---

    refreshSignatureNodes() {
      if (!this.editor) return;
      const { tr } = this.editor.state;
      let modified = false;
      this.editor.state.doc.descendants((node, pos) => {
        if (node.type.name === "signature") {
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            signatureVersion: (node.attrs.signatureVersion || 0) + 1,
          });
          modified = true;
        }
      });
      if (modified) {
        this.editor.view.dispatch(tr);
      }
    },

    insertSignature({
      participant_type_code,
      participant_name,
      participant_type,
    }) {
      if (!this.editor) return;
      this.editor.commands.insertSignature({
        participant_type_code,
        participant_name,
        participant_type: participant_type || "participant",
      });
    },

    // --- Drag Handle ---

    onEditorMouseMove(event) {
      if (!this.editor?.view?.dom || this.mode !== "edit") return;
      if (this.dragHandleLocked) return;

      if (this._dragRaf) return;
      this._dragRaf = requestAnimationFrame(() => {
        this._dragRaf = null;
        this._updateDragHandle(event.clientX, event.clientY);
      });
    },

    _updateDragHandle(clientX, clientY) {
      if (this.dragHandleLocked) return;
      if (!this.editor?.view?.dom) return;

      // Hide when text is selected
      if (!this.editor.state.selection.empty) {
        this._scheduleHide();
        return;
      }

      const editorView = this.editor.view;
      let pos;
      try {
        pos = editorView.posAtCoords({ left: clientX, top: clientY });
      } catch {
        return;
      }

      if (!pos) {
        this._scheduleHide();
        return;
      }

      try {
        const resolved = editorView.state.doc.resolve(pos.pos);
        const blockPos = resolved.before(1);

        if (blockPos < 0) {
          this._scheduleHide();
          return;
        }

        if (blockPos === this._lastDragBlockPos) return;

        const node = editorView.state.doc.nodeAt(blockPos);
        if (!node) {
          this._scheduleHide();
          return;
        }

        const dom = editorView.nodeDOM(blockPos);
        if (!dom || !(dom instanceof HTMLElement)) {
          this._scheduleHide();
          return;
        }

        clearTimeout(this._hideTimer);
        const editorRect = editorView.dom.getBoundingClientRect();
        const domRect = dom.getBoundingClientRect();

        this._lastDragBlockPos = blockPos;
        this.dragHandleNode = node;
        this.dragHandlePos = blockPos;
        this.dragHandleVisible = true;
        this.dragHandlePosition = {
          top: `${domRect.top - editorRect.top}px`,
        };
      } catch {
        this._scheduleHide();
      }
    },

    _scheduleHide() {
      clearTimeout(this._hideTimer);
      this._hideTimer = setTimeout(() => {
        if (!this.dragHandleLocked) {
          this._lastDragBlockPos = -1;
          this.dragHandleVisible = false;
        }
      }, 300);
    },

    hideDragHandle() {
      if (this.dragHandleLocked) return;
      this._scheduleHide();
    },

    lockDragHandle() {
      this.dragHandleLocked = true;
      clearTimeout(this._hideTimer);
    },

    unlockDragHandle() {
      this.dragHandleLocked = false;
    },

    // --- Drag and Drop блоков ---

    onBlockDrop(event) {
      const fromPos = this.editor?._dragFromPos;
      const nodeSize = this.editor?._dragNodeSize;

      this.editor._dragFromPos = null;
      this.editor._dragNodeSize = null;

      if (fromPos == null || !nodeSize) return;

      try {
        const view = this.editor.view;
        const dropCoords = view.posAtCoords({
          left: event.clientX,
          top: event.clientY,
        });
        if (!dropCoords) return;

        const resolved = view.state.doc.resolve(dropCoords.pos);
        let targetPos = resolved.before(1);
        if (targetPos < 0) targetPos = 0;

        // Same position — do nothing
        if (targetPos === fromPos) return;

        // Single transaction: delete + insert
        const { tr } = view.state;
        const node = view.state.doc.nodeAt(fromPos);
        if (!node) return;

        // Copy node before deleting
        const nodeJSON = node.toJSON();

        if (targetPos > fromPos) {
          // Moving down: insert first, then delete
          const insertPos =
            targetPos + view.state.doc.nodeAt(targetPos).nodeSize;
          tr.insert(insertPos, view.state.schema.nodeFromJSON(nodeJSON));
          tr.delete(fromPos, fromPos + nodeSize);
        } else {
          // Moving up: delete first, then insert
          tr.delete(fromPos, fromPos + nodeSize);
          tr.insert(targetPos, view.state.schema.nodeFromJSON(nodeJSON));
        }

        view.dispatch(tr);
      } catch {
        // ignore errors during drag
      }

      this.hideDragHandle();
    },

    // --- Предпросмотр ---

    getPreviewContent() {
      if (!this.editor) return null;
      this.$emit("change:mode", "preview");
    },
  },
};
</script>

<style lang="scss">
.page.a4 {
  width: 21cm;
  max-width: 21cm;
  min-height: 29.7cm;
  margin: 50px auto;

  &.no-top-margin {
    margin-top: 0;
  }
  padding: 2cm;
  background: #fff;
  color: #000;
  font-family: Arial, sans-serif;
  font-size: 11pt;
  line-height: 1.15;
  box-sizing: border-box;
  border-radius: 0.375rem;

  box-shadow: 0 0.54px 1.49px #00000005, 0 1.5px 4.13px #00000008;

  &.dark {
    background: #f5f4f4 !important;
  }
}

.tiptap-wrapper.without-padding {
  @apply p-0 !important;
}

.editor-shell {
  @apply transition-opacity duration-200;
}

@media (max-width: 800px) {
  .editor-shell {
    @apply border-0 rounded-none;
  }

  .page.a4 {
    @apply p-4;
    width: 100%;
    min-height: auto;
  }
}

.tiptap {
  &:focus-visible {
    @apply outline-none;
  }

  :first-child {
    margin-top: 0;
  }

  p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #9ca3af;
    pointer-events: none;
    height: 0;
  }

  /* List styles */
  ul {
    padding: 0 1rem;
    margin: 0.5rem 1rem 0.5rem 0.4rem;
    list-style: disc outside !important;

    li {
      display: list-item !important;

      p {
        margin-top: 0.25em;
        margin-bottom: 0.25em;
      }
    }
  }

  ol {
    padding: 0 1rem;
    margin: 0.5rem 1rem 0.5rem 0.4rem;
    list-style-type: decimal !important;

    li p {
      margin-top: 0.25em;
      margin-bottom: 0.25em;
    }
  }

  /* Heading styles */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.1;
    margin-top: 1rem;
    text-wrap: pretty;
  }

  h1,
  h2 {
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }

  h1 {
    font-size: 1.6rem;
    font-weight: 600;
  }

  h2 {
    font-size: 1.4rem;
    font-weight: 600;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 500;
  }

  h4 {
    font-size: 1.1rem;
    font-weight: 500;
  }

  h5 {
    font-size: 1rem;
    font-weight: 500;
  }

  h6 {
    font-size: 0.9rem;
    font-weight: 500;
  }

  code {
    background-color: #f0f0f0;
    border-radius: 0.4rem;
    color: black;
    font-size: 0.85rem;
    padding: 0.25em 0.3em;
  }

  pre {
    background: #000;
    border-radius: 0.5rem;
    color: white;
    font-family: "JetBrainsMono", monospace;
    margin: 1.5rem 0;
    padding: 0.75rem 1rem;

    code {
      background: none;
      color: inherit;
      font-size: 0.8rem;
      padding: 0;
    }
  }

  blockquote {
    border-left: 3px solid gray;
    margin: 1.5rem 0;
    padding-left: 1rem;
  }

  hr {
    border: none;
    border-top: 1px solid gray;
    margin: 2rem 0;
  }

  a {
    @apply text-muted-foreground underline cursor-pointer;
    &:hover {
      @apply text-blue-600;
    }
    &:active {
      @apply text-blue-700;
    }
    &:focus {
      @apply text-blue-800;
    }
    &:visited {
      @apply text-blue-900;
    }
  }

  table {
    border-collapse: collapse;
    margin: 0;
    overflow: hidden;
    table-layout: fixed;
    width: 100%;

    td,
    th {
      border: 1px solid hsl(var(--border));
      box-sizing: border-box;
      min-width: 1em;
      padding: 6px 8px;
      position: relative;
      vertical-align: top;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      background-color: hsl(var(--muted));
      font-weight: bold;
      text-align: left;
    }

    .selectedCell:after {
      background: hsl(var(--primary) / 0.1);
      content: "";
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      pointer-events: none;
      position: absolute;
      z-index: 2;
    }

    .column-resize-handle {
      background-color: hsl(var(--primary));
      bottom: -2px;
      pointer-events: none;
      position: absolute;
      right: -2px;
      top: 0;
      width: 4px;
    }
  }

  .tableWrapper {
    margin: 2rem 0;
    padding-top: 1.5rem;
    overflow-x: auto;
  }

  &.resize-cursor {
    cursor: col-resize;
  }

  /* Mention node styles */
  .mention-node {
    @apply px-1.5  rounded inline-flex justify-center items-center leading-6;
    @apply text-justify justify-start font-medium text-sm;
    @apply cursor-pointer;
    background: rgba(96, 165, 250, 0.2);
    border: 1px solid rgba(96, 165, 250, 0.4);
    color: #000;
    transition: background 150ms ease, border-color 150ms ease;

    &:hover {
      background: rgba(96, 165, 250, 0.35);
    }

    &[data-value]:not([data-value=""]) {
      color: #000;
      @apply font-bold text-sm;
    }

    &.mention-custom {
      background: rgba(251, 146, 60, 0.2);
      border-color: rgba(251, 146, 60, 0.4);

      &:hover {
        background: rgba(251, 146, 60, 0.35);
      }
    }

    &.mention-notary {
      background: rgba(231, 200, 220, 0.5);
      border-color: rgba(231, 200, 220, 0.7);

      &:hover {
        background: rgba(231, 200, 220, 0.7);
      }
    }

    &.mention-calc {
      background: rgba(168, 85, 247, 0.15);
      border-color: rgba(168, 85, 247, 0.35);

      &:hover {
        background: rgba(168, 85, 247, 0.3);
      }
    }

    &.mention-updated {
      animation: mentionInsert 300ms ease-out;
    }

    &.mention-skeleton {
      @apply animate-pulse;
      color: transparent !important;
      cursor: progress;
      pointer-events: none;
    }
  }

  @keyframes mentionInsert {
    0% {
      background: rgba(34, 197, 94, 0.4);
      border-color: rgba(34, 197, 94, 0.6);
    }
    100% {
      background: rgba(96, 165, 250, 0.2);
      border-color: rgba(96, 165, 250, 0.4);
    }
  }

  .signature-node-wrapper {
    margin: 12px 0;
  }

  .signature-pending {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 16px;
    border: 1px solid #fbbf24;
    border-radius: 8px;
    background: rgba(251, 191, 36, 0.08);
    color: #b45309;
    font-size: 13px;
    font-weight: 500;
  }

  .mention-preview {
    background: none !important;
    border: none !important;
    padding: 0;
    color: inherit;
    font-weight: normal;

    &[data-value]:not([data-value=""]) {
      @apply text-foreground font-semibold text-sm;
    }
  }
}

p,
span,
h1,
h2,
h3,
h4,
h5,
h6 {
  @apply leading-7;
}

.extra-action {
  @apply flex items-center gap-2 h-10 text-foreground;
  @apply px-3.5 py-2.5 rounded-xl border border-border cursor-auto;

  &:hover {
    @apply bg-muted;
  }
}

/* Mention suggestion popup */
.mention-suggestion-popup {
  position: absolute;
  z-index: 9999;
  max-height: 240px;
  overflow-y: auto;
  min-width: 200px;
  max-width: 360px;
  border-radius: 8px;
  border: 1px solid hsl(var(--border));
  background: hsl(var(--popover));
  color: hsl(var(--popover-foreground));
  padding: 4px;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  animation: mentionPopupIn 150ms ease-out;
}

@keyframes mentionPopupIn {
  from {
    opacity: 0;
    transform: translateY(-4px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.mention-suggestion-item {
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  line-height: 1.4;
  color: hsl(var(--foreground));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background 100ms ease;

  &:hover,
  &.is-active {
    background: hsl(var(--muted));
  }

  &.is-active {
    font-weight: 500;
  }
}

/* Slash command popup */
.slash-command-popup {
  position: absolute;
  z-index: 9999;
  min-width: 220px;
  max-height: 400px;
  overflow-y: auto;
  border-radius: var(--radius-xl);
  border: 1px solid hsl(var(--border));
  background: hsl(var(--popover));
  color: hsl(var(--popover-foreground));
  padding: 0.5em;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  animation: mentionPopupIn 150ms ease-out;
}

.slash-command-label {
  padding: 0.5em 0.625em;
  font-size: 13px;
  font-weight: 700;
  color: hsl(var(--foreground));
}

.slash-command-divider {
  height: 1px;
  background: hsl(var(--border));
  margin: 0.375em 0.5em;
}

.slash-command-item {
  display: flex;
  align-items: center;
  gap: 0.625em;
  padding: 0.5em;
  border-radius: 6px;
  cursor: pointer;
  transition: background 100ms ease;

  &:hover,
  &.is-active {
    background: hsl(var(--muted));
  }
}

.slash-command-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5em;
  height: 1.5em;
  flex-shrink: 0;
  color: hsl(var(--muted-foreground));
}

.slash-command-item-title {
  font-size: 15px;
  font-weight: 400;
  color: hsl(var(--foreground));
  line-height: 1.3;
  white-space: nowrap;
}

/* Mobile table styles */
@media (max-width: 800px) {
  .tiptap table {
    font-size: 10pt;

    td,
    th {
      padding: 4px 6px;
    }
  }
}
</style>
